import React, { useEffect, useMemo, useState } from "react";
import logoNew from "../../../assets/logo-collapsed.png";

const IOS_URL = "https://apps.apple.com/us/app/emalpha/id6758406532";
const ANDROID_URL =
  "https://play.google.com/store/apps/details?id=com.emalpha.app&pli=1";

function detectPlatform() {
  const ua = navigator.userAgent || "";
  const platform = navigator.platform || "";
  const maxTouchPoints = navigator.maxTouchPoints || 0;

  const isAndroid = /Android/i.test(ua);

  // iPadOS 13+ often reports as "MacIntel" but has touch points
  const isiOS =
    /iPhone|iPad|iPod/i.test(ua) ||
    (platform === "MacIntel" && maxTouchPoints > 1);

  return { isiOS, isAndroid };
}

function getRedirectUrl({ isiOS, isAndroid }) {
  if (isiOS) return IOS_URL;
  if (isAndroid) return ANDROID_URL;
  return null; // desktop/unknown
}

export default function GetAppRedirectPage() {
  const platform = useMemo(detectPlatform, []);
  const redirectUrl = useMemo(() => getRedirectUrl(platform), [platform]);

  const [secondsLeft, setSecondsLeft] = useState(3);

  useEffect(() => {
    if (!redirectUrl) return;

    // countdown
    const interval = setInterval(() => {
      setSecondsLeft((s) => (s > 1 ? s - 1 : 1));
    }, 1000);

    // redirect after ~2.5s
    const timer = setTimeout(() => {
      window.location.replace(redirectUrl);
    }, 2500);

    return () => {
      clearInterval(interval);
      clearTimeout(timer);
    };
  }, [redirectUrl]);

  const primaryHref = redirectUrl || IOS_URL; // for the big CTA on desktop too
  const primaryLabel = redirectUrl
    ? "Continue to your app store"
    : "Download EMAlpha";

  return (
    <div style={styles.page}>
      {/* soft glowing blobs */}
      <div style={{ ...styles.blob, ...styles.blob1 }} />
      <div style={{ ...styles.blob, ...styles.blob2 }} />

      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.header}>
            {/* Replace this circle with your logo image if you want */}
            {/* <img src={logo} alt="EMAlpha" style={styles.logoImg} /> */}
            <div style={styles.logoMark}><img src={logoNew} height="40" alt="EMAlpha Log" loading="lazy" /></div>

            <div>
              <div style={styles.brand}>EMAlpha</div>
              <div style={styles.tagline}>
                Real-time intelligence for markets.
              </div>
            </div>
          </div>

          <div style={styles.hero}>
            <h1 style={styles.title}>Get the EMAlpha app</h1>
            <p style={styles.subtitle}>
              You are being taken to the app store.
            </p>

            {redirectUrl ? (
              <div style={styles.autoBox}>
                <span style={styles.dotPulse} />
                <span>
                  Redirecting in <b>{secondsLeft}</b>…
                </span>
              </div>
            ) : (
              <div style={styles.autoBox}>
                <span style={styles.dotStatic} />
                <span>
                  You’re on desktop — choose your store below or open this link
                  on mobile for auto-redirect.
                </span>
              </div>
            )}
          </div>

          <div style={styles.ctaRow}>
            <a href={primaryHref} style={styles.primaryBtn}>
              {primaryLabel}
              <span style={styles.arrow}>→</span>
            </a>

            <div style={styles.storeRow}>
              <a href={IOS_URL} style={styles.storeBtn}>
                <span style={styles.storeIcon}></span>
                App Store
              </a>
              <a href={ANDROID_URL} style={styles.storeBtn}>
                <span style={styles.storeIcon}>▶</span>
                Google Play
              </a>
            </div>
          </div>

          <div style={styles.divider} />

          <div style={styles.footer}>
            <div style={styles.helpTitle}>Having trouble?</div>
            <div style={styles.helpText}>
              If you’re opening this inside Instagram/LinkedIn, tap the menu and
              choose <b>Open in Safari/Chrome</b>.
            </div>

            <div style={styles.smallLinks}>
              <a href={IOS_URL} style={styles.smallLink}>
                Open iOS link
              </a>
              <span style={styles.sep}>•</span>
              <a href={ANDROID_URL} style={styles.smallLink}>
                Open Android link
              </a>
            </div>
          </div>

          {/* Optional QR codes (add later if you want)
          <div style={styles.qrGrid}>
            <div style={styles.qrBox}>QR: iOS</div>
            <div style={styles.qrBox}>QR: Android</div>
          </div>
          */}
        </div>

        <div style={styles.bottomNote}>
          © {new Date().getFullYear()} EMAlpha
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    position: "relative",
    overflow: "hidden",
    display: "grid",
    placeItems: "center",
    padding: 24,
    background:
      "radial-gradient(1200px 800px at 20% 10%, rgba(80,120,255,0.35), transparent 60%)," +
      "radial-gradient(900px 700px at 90% 30%, rgba(255,120,200,0.25), transparent 55%)," +
      "linear-gradient(180deg, #070A12 0%, #0B1022 55%, #070A12 100%)",
    fontFamily:
      'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Arial',
    color: "rgba(255,255,255,0.92)",
  },
  blob: {
    position: "absolute",
    filter: "blur(60px)",
    opacity: 0.55,
    borderRadius: 9999,
    transform: "translateZ(0)",
  },
  blob1: {
    width: 420,
    height: 420,
    left: -120,
    top: -120,
    background: "rgba(90,140,255,0.70)",
  },
  blob2: {
    width: 520,
    height: 520,
    right: -180,
    bottom: -180,
    background: "rgba(255,110,200,0.55)",
  },
  container: {
    width: "100%",
    maxWidth: 680,
    display: "grid",
    gap: 16,
    justifyItems: "center",
  },
  card: {
    width: "100%",
    borderRadius: 22,
    padding: 22,
    background: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,255,255,0.12)",
    boxShadow: "0 18px 60px rgba(0,0,0,0.45)",
    backdropFilter: "blur(10px)",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: 18,
  },
  logoMark: {
    width: 44,
    height: 44,
    borderRadius: 14,
    display: "grid",
    placeItems: "center",
    fontWeight: 800,
    fontSize: 18,
    background:
      "white",
    color: "#06102A",
    boxShadow: "0 10px 28px rgba(0,0,0,0.35)",
  },
  brand: { fontSize: 16, fontWeight: 800, letterSpacing: 0.2 },
  tagline: { fontSize: 12, opacity: 0.75, marginTop: 2 },

  hero: { marginTop: 6, marginBottom: 14 },
  title: { margin: "6px 0 6px", fontSize: 28, fontWeight: 850 },
  subtitle: { margin: 0, opacity: 0.78, lineHeight: 1.55 },

  autoBox: {
    marginTop: 14,
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 12px",
    borderRadius: 14,
    background: "rgba(255,255,255,0.08)",
    border: "1px solid rgba(255,255,255,0.10)",
    fontSize: 13,
    opacity: 0.92,
  },
  dotPulse: {
    width: 10,
    height: 10,
    borderRadius: 999,
    background: "rgba(120,200,255,0.95)",
    boxShadow: "0 0 0 0 rgba(120,200,255,0.55)",
    animation: "pulse 1.2s infinite",
  },
  dotStatic: {
    width: 10,
    height: 10,
    borderRadius: 999,
    background: "rgba(255,255,255,0.55)",
  },

  ctaRow: { marginTop: 16, display: "grid", gap: 12 },
  primaryBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    padding: "14px 14px",
    borderRadius: 16,
    textDecoration: "none",
    fontWeight: 800,
    background:
      "linear-gradient(135deg, rgba(120,170,255,0.95), rgba(255,140,220,0.90))",
    color: "#06102A",
    boxShadow: "0 16px 40px rgba(0,0,0,0.35)",
  },
  arrow: { fontSize: 18, marginLeft: 2 },

  storeRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 12,
  },
  storeBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    padding: "12px 12px",
    borderRadius: 16,
    textDecoration: "none",
    fontWeight: 700,
    color: "rgba(255,255,255,0.92)",
    background: "rgba(255,255,255,0.08)",
    border: "1px solid rgba(255,255,255,0.12)",
  },
  storeIcon: { fontSize: 16, opacity: 0.9 },

  divider: {
    height: 1,
    background: "rgba(255,255,255,0.10)",
    margin: "18px 0 14px",
  },

  footer: { display: "grid", gap: 6 },
  helpTitle: { fontWeight: 800, fontSize: 13 },
  helpText: { fontSize: 12, opacity: 0.75, lineHeight: 1.55 },
  smallLinks: { marginTop: 6, fontSize: 12, opacity: 0.9 },
  smallLink: {
    color: "rgba(170,210,255,0.95)",
    textDecoration: "none",
    fontWeight: 700,
  },
  sep: { margin: "0 8px", opacity: 0.4 },

  bottomNote: { fontSize: 12, opacity: 0.55 },

  // QR optional
  qrGrid: {
    marginTop: 14,
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 12,
  },
  qrBox: {
    borderRadius: 16,
    padding: 14,
    background: "rgba(255,255,255,0.06)",
    border: "1px dashed rgba(255,255,255,0.18)",
    textAlign: "center",
    opacity: 0.9,
  },
};

/**
 * Add this CSS once globally (e.g., App.css) for the pulse animation:
 *
 * @keyframes pulse {
 *   0% { box-shadow: 0 0 0 0 rgba(120,200,255,0.55); }
 *   70% { box-shadow: 0 0 0 12px rgba(120,200,255,0); }
 *   100% { box-shadow: 0 0 0 0 rgba(120,200,255,0); }
 * }
 */
